import React, { useEffect, useState } from "react";
import { Row, Col, Card, Badge, ProgressBar } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import {
 PieChart,
 Pie,
 Cell,
 Tooltip,
 Legend,
 BarChart,
 Bar,
 XAxis,
 YAxis,
 CartesianGrid,
 ResponsiveContainer,
} from "recharts";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import { useSelector } from "react-redux";

const COLORS = ["#4e73df", "#1cc88a", "#f6c23e", "#e74a3b", "#36b9cc"];

const BillingOverview = () => {
 const [data, setData] = useState({});
 const [loading, setLoading] = useState(true);
 const location = useLocation();
 const user = useSelector((state) => state.auth?.userDetails || {});

 // Get query parameters
 const queryParams = new URLSearchParams(location.search);
 const workOrder = queryParams.get("workOrder");
 const project = queryParams.get("project");
 const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
 const canAddBilling = currentRole === "admin" || currentRole === "billing";

 useEffect(() => {
 const params = {};
 if (workOrder) params.workOrder = workOrder;
 if (project) params.project = project;

 api
 .get("/billing/overview", { params })
 .then((res) => setData(res.data?.data || {}))
 .catch(() => setData({}))
 .finally(() => setLoading(false));
 }, [workOrder, project]);

 if (loading) {
 return <div className="text-center py-5">Loading Billing Overview…</div>;
 }

 /* ---------------- SAFE DATA ---------------- */
 const summary = data.summary || {
 count: 0,
 totalAmount: 0,
 paidAmount: 0,
 totalDeductions: 0,
 pendingAmount: 0,
 };

 const statusBreakdown = data.statusBreakdown || [];
 const stageBreakdown = data.stageBreakdown || [];
 const recent = data.recent || [];
 const metadata = data.metadata || {};

 const collectionPercent =
 summary.totalAmount > 0
 ? Math.round(((Number(summary.paidAmount || 0) + Number(summary.totalDeductions || 0)) / summary.totalAmount) * 100)
 : 0;

 const avgInvoice =
 summary.count > 0
 ? Math.round(summary.totalAmount / summary.count)
 : 0;

 const paymentDonut = [
 { name: "Paid", value: summary.paidAmount },
 ...(summary.totalDeductions > 0 ? [{ name: "Deductions", value: summary.totalDeductions }] : []),
 { name: "Pending", value: summary.pendingAmount },
 ];

 return (
 <>
 <PageTitle activeMenu="Billing Overview" motherMenu="Billing" />

 {/* ================= HEADER CARD (CONTEXT) ================= */}
 {(metadata.workOrder || metadata.project) && (
 <Card className="mb-4 shadow-sm">
 <Card.Body>
 <h5 className="fw-bold mb-1">
 {metadata.workOrder?.woNo || metadata.project?.projectName}
 </h5>
 <small className="text-muted">
 {metadata.workOrder?.projectName || "Billing Overview"}
 </small>
 </Card.Body>
 </Card>
 )}

 {/* ================= KPI CARDS ================= */}
 <Row className="mb-4 g-3">
 {[
 { label: "Invoices", value: summary.count },
 { label: "Total Billed", value: `₹ ${summary.totalAmount.toLocaleString()}` },
 { label: "Paid", value: `₹ ${summary.paidAmount.toLocaleString()}` },
 { label: "Pending", value: `₹ ${summary.pendingAmount.toLocaleString()}` },
 { label: "Collection %", value: `${collectionPercent}%` },
 { label: "Avg Invoice", value: `₹ ${avgInvoice.toLocaleString()}` },
 ].map((c, i) => (
 <Col md={4} lg={2} key={i}>
 <Card className="shadow-sm text-center h-100">
 <Card.Body>
 <small className="text-muted">{c.label}</small>
 <h5 className="fw-bold mt-1">{c.value}</h5>
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>

 {/* ================= ACTION BAR ================= */}
 <div className="d-flex flex-wrap gap-2 mb-4">
 {canAddBilling && (
 <Link 
 to={`/billings/add${location.search}`} 
 className="btn btn-primary btn-sm"
 >
 + Add Billing
 </Link>
 )}
 <Link 
 to={`/billings${location.search}`} 
 className="btn btn-outline-primary btn-sm"
 >
 View All Billings
 </Link>
 </div>

 {/* ================= CHARTS ================= */}
 <Row className="mb-4 g-3">
 {/* Status Pie */}
 <Col lg={4}>
 <Card className="shadow-sm h-100">
 <Card.Header>Status Distribution</Card.Header>
 <Card.Body>
 <ResponsiveContainer width="100%" height={260}>
 <PieChart>
 <Pie
 data={statusBreakdown}
 dataKey="value"
 nameKey="name"
 outerRadius={90}
 >
 {statusBreakdown.map((_, i) => (
 <Cell key={i} fill={COLORS[i % COLORS.length]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 </Card.Body>
 </Card>
 </Col>

 {/* Stage Pie */}
 <Col lg={4}>
 <Card className="shadow-sm h-100">
 <Card.Header>Stage Distribution</Card.Header>
 <Card.Body>
 <ResponsiveContainer width="100%" height={260}>
 <PieChart>
 <Pie
 data={stageBreakdown}
 dataKey="value"
 nameKey="name"
 outerRadius={90}
 >
 {stageBreakdown.map((_, i) => (
 <Cell key={i} fill={COLORS[i % COLORS.length]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 </Card.Body>
 </Card>
 </Col>

 {/* Paid vs Pending */}
 <Col lg={4}>
 <Card className="shadow-sm h-100">
 <Card.Header>Payment Health</Card.Header>
 <Card.Body>
 <ResponsiveContainer width="100%" height={260}>
 <PieChart>
 <Pie
 data={paymentDonut}
 dataKey="value"
 nameKey="name"
 innerRadius={50}
 outerRadius={90}
 >
 <Cell fill="#1cc88a" />
 <Cell fill="#e74a3b" />
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>

 <div className="mt-3">
 <ProgressBar now={collectionPercent} label={`${collectionPercent}%`} />
 </div>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ================= RECENT INVOICES ================= */}
 <Card className="shadow-sm">
 <Card.Header>Recent Invoices</Card.Header>
 <Card.Body>
 {recent.length === 0 ? (
 <p className="text-muted">No recent invoices</p>
 ) : (
 recent.map((b) => (
 <div
 key={b._id}
 className="d-flex justify-content-between align-items-center border-bottom py-2"
 >
 <div>
 <strong>{b.invoiceNumber}</strong>
 <div className="text-muted small">
 {new Date(b.createdAt).toLocaleDateString()}
 </div>
 </div>
 <div className="text-end">
 <Badge bg="info">{b.status}</Badge>
 <div className="fw-bold">
 ₹ {b.totalAmount.toLocaleString()}
 </div>
 </div>
 </div>
 ))
 )}
 </Card.Body>
 </Card>
 </>
 );
};

export default BillingOverview;
